import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarHeart,
  Clock,
  Cog,
  Eye,
  Hash,
  Plus,
  RotateCcw,
  Trash2,
  Settings2,
  ShieldCheck,
  Timer,
  Building2,
  Wallet,
} from "lucide-react";
import { AuthContext } from "../../Context/AuthContext";
import { useToast } from "../../Context/ToastContext";
import API from "../../../services/api";
import { InputWithIcon, SelectWithIcon } from "../../../components/ui/FieldWithIcon";
import { v2ListComponents } from "./SalaryApiV2";
import {
  DEFAULT_OT_COUNT_RULES,
  OT_ALLOCATION_METHODS,
  OT_HOURS_SOURCES,
  OT_METHODS,
  OT_PAY_RATE_PRESETS,
  OT_ROUND_OPTIONS,
  OCCURRENCE_EXPAND_ROLE_OPTIONS,
  DEFAULT_OCCURRENCE_SLOTS,
  MAX_OCCURRENCE_SLOTS,
  PERCENTAGE_APPLIED_ON,
  OVERTIME_FORMULA_BUILDER_PATH,
  PREMIUM_WORKS_PATH,
  SALARY_COMPONENTS,
  allocateOtQuantity,
  buildOtPayPreview,
  formatOtQty,
  configsEqual,
  defaultExtraWorkConfig,
  extraWorkSummary,
  validateOvertimeConfig,
  withDefaultOvertimeSections,
} from "./extraWorkPolicy";
import "../../../styles/extraWorkConfig.css";
import {
  broadcastOtHoursAllocationEnabled,
  broadcastOtHoursAllocationFromExtraWork,
} from "../../attendance/otHoursAllocationEvents";
import { navigateToSettingsHome } from "../../../components/settings/settingsExit";

function Field({ label, required, error, hint, children, full }) {
  return (
    <div className={`ewc-field${full ? " ewc-field--full" : ""}`}>
      <label className="ewc-label">
        {label}
        {required ? <span className="ewc-req">*</span> : null}
      </label>
      {children}
      {error ? <p className="ewc-error">{error}</p> : null}
      {!error && hint ? <p className="ewc-hint">{hint}</p> : null}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label className="ewc-toggle">
      <div>
        <span className="ewc-toggle__label">{label}</span>
        {hint ? <span className="ewc-toggle__hint">{hint}</span> : null}
      </div>
      <input
        type="checkbox"
        className="ewc-check"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

function upsertDepartmentRate(rows, departmentId, field, value) {
  const next = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row }));
  const idx = next.findIndex((row) => row.department_id === departmentId);
  const blank = value === "" || value == null;
  if (blank) {
    return idx >= 0 ? next.filter((row) => row.department_id !== departmentId) : next;
  }
  const numeric = Number(value);
  if (idx >= 0) {
    next[idx] = { ...next[idx], department_id: departmentId, [field]: numeric };
    return next;
  }
  return [...next, { department_id: departmentId, rate_pct: null, fixed_amount: null, [field]: numeric }];
}

function DepartmentRateList({ departments, ot, disabled, onChange }) {
  const amountMode = ot.method === "fixed_rate";
  const field = amountMode ? "fixed_amount" : "rate_pct";
  const rows = Array.isArray(ot.department_rates) ? ot.department_rates : [];
  const active = departments.filter((dept) => dept && (dept.id || dept.department_id));

  if (!active.length) {
    return (
      <p className="ewc-hint" style={{ marginTop: 12 }}>
        This organisation has no departments yet. Add them under Organisation settings, then pick a rate here.
      </p>
    );
  }

  return (
    <div className="ewc-dept-list">
      <p className="ewc-hint">
        {amountMode
          ? "Set the rupee amount this department is paid for overtime. Blank means the organisation amount."
          : "Set the percent of the organisation calculation. Blank means the organisation percent."}
      </p>
      {active.map((dept) => {
        const id = String(dept.id || dept.department_id);
        const row = rows.find((item) => String(item.department_id) === id);
        const current = row && row[field] != null && row[field] !== "" ? Number(row[field]) : null;
        return (
          <div key={id} className="ewc-dept-row">
            <div className="ewc-dept-row__name">
              <strong>{dept.name}</strong>
              {current == null ? <span>Organisation rate</span> : <span>{amountMode ? `₹${current}` : `${current}%`}</span>}
            </div>
            {amountMode ? (
              <label className="ewc-dept-custom">
                Amount (₹)
                <input
                  type="number"
                  min={1}
                  disabled={disabled}
                  value={current ?? ""}
                  placeholder="Same as organisation"
                  onChange={(e) => onChange(upsertDepartmentRate(rows, id, field, e.target.value))}
                />
              </label>
            ) : (
              <div className="ewc-dept-rates">
                {OT_PAY_RATE_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={`ewc-dept-chip${current === preset.value ? " ewc-dept-chip--on" : ""}`}
                    disabled={disabled}
                    onClick={() => onChange(upsertDepartmentRate(rows, id, field, preset.value))}
                  >
                    {preset.value}%
                  </button>
                ))}
                <label className="ewc-dept-custom">
                  Custom %
                  <input
                    type="number"
                    min={1}
                    max={ot.method === "percentage_of_component" ? 200 : 500}
                    disabled={disabled}
                    value={current ?? ""}
                    placeholder="Org"
                    onChange={(e) => onChange(upsertDepartmentRate(rows, id, field, e.target.value))}
                  />
                </label>
                {current != null ? (
                  <button
                    type="button"
                    className="ewc-dept-clear"
                    disabled={disabled}
                    onClick={() => onChange(upsertDepartmentRate(rows, id, field, ""))}
                  >
                    Use organisation rate
                  </button>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function upsertDepartmentHours(rows, departmentId, value) {
  const next = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row }));
  const idx = next.findIndex((row) => row.department_id === departmentId);
  if (value === "" || value == null) {
    return idx >= 0 ? next.filter((row) => row.department_id !== departmentId) : next;
  }
  const numeric = Number(value);
  if (idx >= 0) {
    next[idx] = { ...next[idx], department_id: departmentId, working_hours: numeric };
    return next;
  }
  return [...next, { department_id: departmentId, working_hours: numeric }];
}

function DepartmentHoursList({ departments, ot, disabled, onChange }) {
  const rows = Array.isArray(ot.department_hours) ? ot.department_hours : [];
  const active = departments.filter((dept) => dept && (dept.id || dept.department_id));
  const fallback = ot.working_hours || 8;
  if (!active.length) {
    return (
      <p className="ewc-hint" style={{ marginTop: 12 }}>
        This organisation has no departments yet. Add them under Organisation settings.
      </p>
    );
  }
  return (
    <div className="ewc-dept-list">
      <p className="ewc-hint">Enter how many hours equal one working day. Blank keeps the organisation hours ({fallback}).</p>
      {active.map((dept) => {
        const id = String(dept.id || dept.department_id);
        const row = rows.find((item) => String(item.department_id) === id);
        const current = row && row.working_hours != null && row.working_hours !== "" ? row.working_hours : "";
        return (
          <div key={id} className="ewc-dept-row">
            <div className="ewc-dept-row__name">
              <strong>{dept.name}</strong>
              <span>{current === "" ? `${fallback} hours = 1 day` : `${current} hours = 1 day`}</span>
            </div>
            <label className="ewc-dept-custom">
              Working hours
              <input
                type="number"
                min={1}
                max={24}
                step={0.5}
                disabled={disabled}
                value={current}
                placeholder={String(fallback)}
                onChange={(e) => onChange(upsertDepartmentHours(rows, id, e.target.value))}
              />
            </label>
          </div>
        );
      })}
    </div>
  );
}

function optionsFromCatalog(catalogList, fallback) {
  if (Array.isArray(catalogList) && catalogList.length) return catalogList;
  return fallback;
}

function mergeLoadedConfig(cfg) {
  return {
    ...defaultExtraWorkConfig(),
    ...cfg,
    overtime: {
      ...defaultExtraWorkConfig().overtime,
      ...(cfg.overtime || {}),
      enabled:
        typeof cfg.overtime?.enabled === "boolean"
          ? cfg.overtime.enabled
          : Boolean(cfg.processing?.ot_hours_allocation_enabled),
    },
    processing: {
      ...defaultExtraWorkConfig().processing,
      ...(cfg.processing || {}),
      ot_hours_allocation_enabled:
        typeof cfg.overtime?.enabled === "boolean"
          ? cfg.overtime.enabled
          : Boolean(cfg.processing?.ot_hours_allocation_enabled),
    },
  };
}

export default function OvertimeWorksPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: me } = useContext(AuthContext);
  const role = (me?.role || me?.user?.role || "").toLowerCase();
  const canEdit = role === "admin" || role === "hr";
  const isSuperAdmin = ["superadmin", "super_admin", "admin"].includes(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultExtraWorkConfig);
  const [saved, setSaved] = useState(defaultExtraWorkConfig);
  const [errors, setErrors] = useState({});
  const [catalog, setCatalog] = useState({});
  const [earningCodes, setEarningCodes] = useState([]);
  const [departments, setDepartments] = useState([]);

  const dirty = !configsEqual(form, saved);
  const summary = useMemo(() => extraWorkSummary(form), [form]);
  const roundMethods = optionsFromCatalog(catalog.ot_round_methods, OT_ROUND_OPTIONS);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const [cfgRes, components, deptRes] = await Promise.all([
          API.get("/organisation/me/extra-work-config"),
          v2ListComponents().catch(() => []),
          API.get("/setup/departments").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const deptList = Array.isArray(deptRes?.data) ? deptRes.data : [];
        setDepartments(deptList.filter((d) => d.is_active !== false));
        const cfg = cfgRes.data?.extra_work || defaultExtraWorkConfig();
        const persisted = mergeLoadedConfig(cfg);
        const draftExpr = String(location.state?.otFormulaDraft?.expression || "").trim();
        const merged = draftExpr
          ? {
              ...persisted,
              overtime: {
                ...persisted.overtime,
                method: "formula",
                formula_expression: draftExpr,
                formula_template: location.state.otFormulaDraft.templateId || persisted.overtime.formula_template || "",
              },
            }
          : persisted;
        setForm(merged);
        setSaved(persisted);
        broadcastOtHoursAllocationFromExtraWork(persisted);
        setCatalog(cfgRes.data?.catalog || {});
        const list = Array.isArray(components) ? components : [];
        setEarningCodes(
          list
            .filter((r) => r.is_active !== false)
            .map((r) => String(r.code || "").toUpperCase())
            .filter((code) => code && code !== "OVERTIME" && code !== "OT")
        );
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to load Overtime configuration");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [toast, location.state]);

  useEffect(() => {
    if (!dirty) return undefined;
    function onLeave(e) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  const patch = (section, updates) => {
    setForm((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), ...updates } }));
    setErrors((e) => {
      const next = { ...e };
      Object.keys(updates || {}).forEach((k) => {
        delete next[k];
      });
      return next;
    });
  };

  async function persist(payload, { restoreDefault = false } = {}) {
    if (!canEdit) {
      toast.error("You do not have permission to save this configuration.");
      return;
    }
    try {
      setSaving(true);
      const res = await API.put("/organisation/me/extra-work-config", {
        extra_work: payload,
        restore_default: restoreDefault,
      });
      const cfg = res.data?.extra_work || payload;
      const merged = mergeLoadedConfig(cfg);
      setForm(merged);
      setSaved(merged);
      setErrors({});
      toast.success(
        restoreDefault
          ? "Default Overtime policy restored. Attendance Overtime visibility follows Enable Overtime."
          : "Overtime Works saved. Approved OT hours from Attendance use this pay policy."
      );
      broadcastOtHoursAllocationFromExtraWork(merged);
      navigateToSettingsHome(navigate, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0] || "Please correct the highlighted fields");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save Overtime configuration");
      }
    } finally {
      setSaving(false);
    }
  }

  function onSave() {
    const nextErrors = validateOvertimeConfig(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error("Please correct the highlighted fields before saving.");
      return;
    }
    persist(form);
  }

  function onCancel() {
    if (dirty && !window.confirm("Discard unsaved Overtime changes?")) return;
    navigate("/settings");
  }

  function onReset() {
    if (!window.confirm("Reset this page to the last saved Overtime policy?")) return;
    setForm(saved);
    broadcastOtHoursAllocationFromExtraWork(saved);
    setErrors({});
  }

  function onRestoreDefault() {
    if (!window.confirm("Restore default Overtime settings? Premium Works settings will stay unchanged.")) return;
    persist(withDefaultOvertimeSections(saved), { restoreDefault: false });
  }

  const disabled = !canEdit || loading || saving;
  const ot = form.overtime || {};
  const elig = form.eligibility || {};
  const proc = form.processing || {};
  const otEnabled = Boolean(ot.enabled);
  const hoursSource = ot.hours_source || "automatic";
  const eligibilityApplies = hoursSource !== "manual";
  const eligibilityLocked = otEnabled && !eligibilityApplies;
  const eligibilityDisabled = disabled || !otEnabled || !eligibilityApplies;
  const otPreview = useMemo(() => (otEnabled ? buildOtPayPreview(form) : { lines: [], amount: 0 }), [form, otEnabled]);

  function setOtEnabled(v) {
    patch("overtime", { enabled: v });
    patch("processing", { ot_hours_allocation_enabled: v });
    broadcastOtHoursAllocationEnabled(v);
  }

  function patchOt(updates) {
    patch("overtime", updates);
  }

  function selectAllocation(value) {
    if (value === "custom") {
      const rules = Array.isArray(ot.count_rules) && ot.count_rules.length ? ot.count_rules : DEFAULT_OT_COUNT_RULES;
      const active = rules.some((rule) => rule.id === ot.active_count_rule_id) ? ot.active_count_rule_id : rules[0].id;
      patchOt({
        allocation_method: "custom",
        count_rules: rules,
        active_count_rule_id: active,
        record_hours_with_count: true,
      });
      return;
    }
    if (value === "ot_count") {
      patchOt({
        allocation_method: "ot_count",
        count_per_entry: Number(ot.count_per_entry) > 0 ? Number(ot.count_per_entry) : 1,
        record_hours_with_count: true,
      });
      return;
    }
    patchOt({ allocation_method: "hours" });
  }

  function updateCountRule(id, updates) {
    patchOt({
      count_rules: (ot.count_rules || []).map((rule) => (rule.id === id ? { ...rule, ...updates } : rule)),
    });
  }

  function addCountRule() {
    const rules = Array.isArray(ot.count_rules) ? ot.count_rules : [];
    if (rules.length >= 8) return;
    const id = `rule-${Date.now()}`;
    patchOt({
      count_rules: [...rules, { id, name: "New OT count", hours: 1, ot_count: 1 }],
      active_count_rule_id: id,
      allocation_method: "custom",
    });
  }

  function removeCountRule(id) {
    const rules = (ot.count_rules || []).filter((rule) => rule.id !== id);
    patchOt({
      count_rules: rules,
      active_count_rule_id: rules.some((rule) => rule.id === ot.active_count_rule_id) ? ot.active_count_rule_id : rules[0]?.id || "",
    });
  }

  const allocationMethod = ot.allocation_method || "hours";
  const sampleShort = allocateOtQuantity(1.1, form);
  const sampleLong = allocateOtQuantity(2.2, form);

  return (
    <div className="ewc-root">
      <p className="ewc-lead">
        Configure overtime: enable OT, choose where hours come from, choose how those hours are allocated,
        then choose how approved OT is paid. Sunday / holiday premium and Comp-Off live on{" "}
        <Link to={PREMIUM_WORKS_PATH}>Premium Works</Link>. Attendance Overtime stays under Attendance.
      </p>

      {dirty ? (
        <div className="ewc-warn" role="status">
          You have unsaved Overtime policy changes.
        </div>
      ) : null}

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-500">Loading Overtime configuration…</p>
      ) : (
        <div className="ewc-page">
          <div className="ewc-stack">
            <section className="ewc-card ewc-card--ot-hero">
              <div className="ewc-card__head">
                <Timer className="ewc-card__icon text-sky-600" aria-hidden />
                <div>
                  <h3>Overtime</h3>
                  <p>Attendance records OT hours. Payroll calculates OT money. Keep these two concepts separate.</p>
                </div>
              </div>

              <div className="ewc-step">
                <span className="ewc-step__num">1</span>
                <div className="ewc-step__body">
                  <Toggle
                    label="Enable Overtime"
                    hint="Default OFF for new organisations — OT will not affect attendance or payroll until you enable it."
                    checked={otEnabled}
                    disabled={disabled}
                    onChange={setOtEnabled}
                  />
                  {!otEnabled ? (
                    <div className="ewc-disabled-banner" role="status">
                      Overtime is currently disabled.
                      Enable overtime to choose how OT is recorded, allocated, and paid.
                    </div>
                  ) : null}
                </div>
              </div>

              {otEnabled ? (
                <>
                  <div className="ewc-step">
                    <span className="ewc-step__num">2</span>
                    <div className="ewc-step__body">
                      <p className="ewc-step__title">How should OT hours be determined?</p>
                      <p className="ewc-hint" style={{ marginBottom: 10 }}>
                        This is the <strong>OT Hours Source</strong> — where OT hours come from. It is not the pay method.
                      </p>
                      <div className="ewc-choice-grid" role="radiogroup" aria-label="OT Hours Source">
                        {OT_HOURS_SOURCES.map((opt) => {
                          const selected = (ot.hours_source || "automatic") === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`ewc-choice${selected ? " ewc-choice--on" : ""}`}
                              disabled={disabled}
                              onClick={() => patchOt({ hours_source: opt.value })}
                              aria-pressed={selected}
                            >
                              <span className="ewc-choice__radio" aria-hidden />
                              <span className="ewc-choice__label">{opt.label}</span>
                              <span className="ewc-choice__hint">{opt.hint}</span>
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const src = OT_HOURS_SOURCES.find((o) => o.value === (ot.hours_source || "automatic"));
                        if (!src) return null;
                        return (
                          <ul className="ewc-checks">
                            {src.checks.map((c) => (
                              <li key={c}>✓ {c}</li>
                            ))}
                          </ul>
                        );
                      })()}
                      {errors.ot_hours_source ? <p className="ewc-error">{errors.ot_hours_source}</p> : null}
                    </div>
                  </div>

                  <div className="ewc-step">
                    <span className="ewc-step__num">3</span>
                    <div className="ewc-step__body">
                      <p className="ewc-step__title">How should overtime be allocated?</p>
                      <p className="ewc-hint" style={{ marginBottom: 10 }}>
                        This decides the OT unit. Hours are calculated when you choose hours. OT entries are counted when you choose an OT count.
                      </p>
                      <div className="ewc-alloc-grid" role="radiogroup" aria-label="OT allocation method">
                        {OT_ALLOCATION_METHODS.map((opt) => {
                          const selected = allocationMethod === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              className={`ewc-alloc${selected ? " ewc-alloc--on" : ""}`}
                              disabled={disabled}
                              onClick={() => selectAllocation(opt.value)}
                              aria-pressed={selected}
                            >
                              <span className="ewc-alloc__kicker">{opt.kicker}</span>
                              <span className="ewc-alloc__label">{opt.label}</span>
                              <span className="ewc-alloc__rule">{opt.rule}</span>
                            </button>
                          );
                        })}
                      </div>
                      {errors.ot_allocation_method ? <p className="ewc-error">{errors.ot_allocation_method}</p> : null}

                      <div className="ewc-alloc-samples" aria-label="Allocation examples">
                        <div className="ewc-alloc-sample">
                          <span>Worked 1.1 hours</span>
                          <strong>
                            {sampleShort.unit === "hours"
                              ? `${formatOtQty(sampleShort.payableUnits)} OT hours`
                              : `${formatOtQty(sampleShort.payableUnits)} OT`}
                          </strong>
                          <em>
                            {allocationMethod === "hours"
                              ? "1 hour = 1 OT hour, so 1.1 stays 1.1."
                              : allocationMethod === "ot_count"
                                ? `One entry = ${formatOtQty(ot.count_per_entry || 1)} OT. Hours 1.1 stay on that count.`
                                : sampleShort.payableUnits > 0
                                  ? `${sampleShort.ruleName}: ${formatOtQty(sampleShort.blockHours)} hours = ${formatOtQty(sampleShort.blockCount)} OT.`
                                  : `${sampleShort.ruleName || "This block"} needs more than 1.1 hours. Remainder stays on the record.`}
                          </em>
                        </div>
                        <div className="ewc-alloc-sample">
                          <span>Worked 2.2 hours</span>
                          <strong>
                            {sampleLong.unit === "hours"
                              ? `${formatOtQty(sampleLong.payableUnits)} OT hours`
                              : `${formatOtQty(sampleLong.payableUnits)} OT`}
                          </strong>
                          <em>
                            {allocationMethod === "ot_count"
                              ? "Still one OT entry. The count does not grow with the hours."
                              : sampleLong.remainderHours > 0
                                ? `${formatOtQty(sampleLong.remainderHours)}h left on the record, not counted as another OT.`
                                : "The full worked time is included in the allocated unit."}
                          </em>
                        </div>
                      </div>

                      {allocationMethod === "ot_count" ? (
                        <div className="ewc-alloc-panel">
                          <Field
                            label="OT count for each entry"
                            required
                            error={errors.ot_count_per_entry}
                            hint="1 OT = 1 OT by default. Raise this only when one entry should award more than one OT."
                          >
                            <InputWithIcon
                              icon={Hash}
                              type="number"
                              min={1}
                              step={1}
                              value={ot.count_per_entry ?? 1}
                              disabled={disabled}
                              onChange={(e) => patchOt({ count_per_entry: e.target.value, record_hours_with_count: true })}
                            />
                          </Field>
                          <p className="ewc-hint">
                            Hours are saved on the count. A 1.1 hour entry is still {formatOtQty(ot.count_per_entry || 1)} OT, and Attendance shows both the count and the 1.1 hours.
                          </p>
                        </div>
                      ) : null}

                      {allocationMethod === "custom" ? (
                        <div className="ewc-alloc-panel">
                          <div className="ewc-alloc-panel__head">
                            <div>
                              <p className="ewc-step__title">OT count methods</p>
                              <p className="ewc-hint">
                                Pick the block payroll should use. Each full block of hours awards the OT count. Add another method when the organisation uses more than one block.
                              </p>
                            </div>
                            <button type="button" className="ewc-btn ewc-btn--ghost" disabled={disabled || (ot.count_rules || []).length >= 8} onClick={addCountRule}>
                              <Plus size={14} /> Add OT count method
                            </button>
                          </div>
                          <div className="ewc-rule-list">
                            {(ot.count_rules || []).map((rule) => {
                              const active = (ot.active_count_rule_id || (ot.count_rules || [])[0]?.id) === rule.id;
                              const preview = allocateOtQuantity(2.2, {
                                overtime: { ...ot, allocation_method: "custom", active_count_rule_id: rule.id },
                              });
                              return (
                                <div key={rule.id} className={`ewc-rule${active ? " ewc-rule--on" : ""}`}>
                                  <button
                                    type="button"
                                    className="ewc-rule__pick"
                                    disabled={disabled}
                                    onClick={() => patchOt({ active_count_rule_id: rule.id })}
                                    aria-pressed={active}
                                  >
                                    <span className="ewc-choice__radio" aria-hidden />
                                    {active ? "In use" : "Use this"}
                                  </button>
                                  <label className="ewc-rule__field">
                                    <span>Method name</span>
                                    <input
                                      value={rule.name || ""}
                                      disabled={disabled}
                                      onChange={(e) => updateCountRule(rule.id, { name: e.target.value })}
                                    />
                                  </label>
                                  <label className="ewc-rule__field">
                                    <span>Hours</span>
                                    <input
                                      type="number"
                                      min={0.25}
                                      step={0.25}
                                      value={rule.hours ?? 1}
                                      disabled={disabled}
                                      onChange={(e) => updateCountRule(rule.id, { hours: e.target.value })}
                                    />
                                  </label>
                                  <span className="ewc-rule__eq">=</span>
                                  <label className="ewc-rule__field">
                                    <span>OT count</span>
                                    <input
                                      type="number"
                                      min={1}
                                      step={1}
                                      value={rule.ot_count ?? 1}
                                      disabled={disabled}
                                      onChange={(e) => updateCountRule(rule.id, { ot_count: e.target.value })}
                                    />
                                  </label>
                                  <p className="ewc-rule__example">
                                    2.2 hours → {formatOtQty(preview.payableUnits)} OT
                                    {preview.remainderHours > 0 ? ` · ${formatOtQty(preview.remainderHours)}h kept on the record` : ""}
                                  </p>
                                  <button
                                    type="button"
                                    className="ewc-rule__remove"
                                    disabled={disabled || (ot.count_rules || []).length <= 1}
                                    onClick={() => removeCountRule(rule.id)}
                                    aria-label={`Remove ${rule.name || "OT count method"}`}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {errors.ot_count_rules ? <p className="ewc-error">{errors.ot_count_rules}</p> : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="ewc-step">
                    <span className="ewc-step__num">4</span>
                    <div className="ewc-step__body">
                      <p className="ewc-step__title">How should approved OT be paid?</p>
                      <p className="ewc-hint" style={{ marginBottom: 10 }}>
                        Only <strong>Approved</strong> OT from Attendance enters payroll. Pending / Rejected / Cancelled are not paid.
                      </p>
                      <Field label="OT Pay Calculation Method" required error={errors.ot_method}>
                        <SelectWithIcon
                          icon={Timer}
                          value={ot.method || ""}
                          disabled={disabled}
                          onChange={(e) => patchOt({ method: e.target.value })}
                        >
                          {OT_METHODS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </SelectWithIcon>
                      </Field>

                      {ot.method === "percentage_of_component" ? (
                        <div className="ewc-grid" style={{ marginTop: 12 }}>
                          <Field label="Based On" required hint="Salary basis for the OT percentage">
                            <SelectWithIcon
                              icon={Wallet}
                              value={ot.salary_component || "BASIC"}
                              disabled={disabled}
                              onChange={(e) => patchOt({ salary_component: e.target.value })}
                            >
                              {SALARY_COMPONENTS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </SelectWithIcon>
                          </Field>
                          {ot.salary_component === "SELECTED" ? (
                            <Field label="Select Components" required full error={errors.ot_selected_components}>
                              <SelectWithIcon
                                icon={Wallet}
                                value=""
                                disabled={disabled}
                                onChange={(e) => {
                                  const code = e.target.value;
                                  if (!code) return;
                                  const cur = Array.isArray(ot.selected_component_codes) ? [...ot.selected_component_codes] : [];
                                  if (!cur.includes(code)) cur.push(code);
                                  patchOt({ selected_component_codes: cur });
                                }}
                              >
                                <option value="">Add earning code…</option>
                                {earningCodes.map((code) => (
                                  <option key={code} value={code}>{code}</option>
                                ))}
                              </SelectWithIcon>
                              <div className="ewc-chips">
                                {(ot.selected_component_codes || []).map((code) => (
                                  <button
                                    key={code}
                                    type="button"
                                    className="ewc-chip"
                                    disabled={disabled}
                                    onClick={() =>
                                      patchOt({
                                        selected_component_codes: (ot.selected_component_codes || []).filter((c) => c !== code),
                                      })
                                    }
                                  >
                                    {code} ×
                                  </button>
                                ))}
                              </div>
                            </Field>
                          ) : null}
                          {ot.salary_component === "OTHER" ? (
                            <Field label="Other component code" required full error={errors.ot_component_other}>
                              <SelectWithIcon
                                icon={Wallet}
                                value={ot.salary_component_other || ""}
                                disabled={disabled}
                                onChange={(e) => patchOt({ salary_component_other: e.target.value })}
                              >
                                <option value="">Select earning code</option>
                                {earningCodes.map((code) => (
                                  <option key={code} value={code}>{code}</option>
                                ))}
                              </SelectWithIcon>
                            </Field>
                          ) : null}
                          <Field
                            label="Percentage Applied On"
                            required
                            error={errors.ot_percentage_applied_on}
                            hint="Choose the amount/unit to which the OT percentage will be applied."
                          >
                            <SelectWithIcon
                              icon={Settings2}
                              value={ot.percentage_applied_on || "per_ot_hour"}
                              disabled={disabled}
                              onChange={(e) => patchOt({ percentage_applied_on: e.target.value })}
                            >
                              {PERCENTAGE_APPLIED_ON.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </SelectWithIcon>
                          </Field>
                          <Field label="OT Percentage" required error={errors.ot_percentage}>
                            <InputWithIcon
                              icon={Settings2}
                              type="number"
                              min={0}
                              max={200}
                              step={1}
                              value={ot.percentage ?? 20}
                              disabled={disabled}
                              onChange={(e) => patchOt({ percentage: e.target.value })}
                            />
                          </Field>
                        </div>
                      ) : null}

                      {ot.method === "hourly_rate" ? (
                        <div className="ewc-grid" style={{ marginTop: 12 }}>
                          <Field label="Hourly Rate Based On" required>
                            <SelectWithIcon
                              icon={Wallet}
                              value={ot.salary_component || "BASIC"}
                              disabled={disabled}
                              onChange={(e) => patchOt({ salary_component: e.target.value })}
                            >
                              {SALARY_COMPONENTS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </SelectWithIcon>
                          </Field>
                          {ot.salary_component === "SELECTED" ? (
                            <Field label="Select Components" required full error={errors.ot_selected_components}>
                              <SelectWithIcon
                                icon={Wallet}
                                value=""
                                disabled={disabled}
                                onChange={(e) => {
                                  const code = e.target.value;
                                  if (!code) return;
                                  const cur = Array.isArray(ot.selected_component_codes) ? [...ot.selected_component_codes] : [];
                                  if (!cur.includes(code)) cur.push(code);
                                  patchOt({ selected_component_codes: cur });
                                }}
                              >
                                <option value="">Add earning code…</option>
                                {earningCodes.map((code) => (
                                  <option key={code} value={code}>{code}</option>
                                ))}
                              </SelectWithIcon>
                            </Field>
                          ) : null}
                          <Field label="Working Days" required error={errors.ot_working_days} hint="Monthly Amount ÷ Working Days ÷ Working Hours">
                            <InputWithIcon
                              icon={CalendarHeart}
                              type="number"
                              min={1}
                              value={ot.working_days ?? 26}
                              disabled={disabled}
                              onChange={(e) => patchOt({ working_days: e.target.value })}
                            />
                          </Field>
                          <Field label="Working Hours / Day" required error={errors.ot_working_hours}>
                            <InputWithIcon
                              icon={Clock}
                              type="number"
                              min={1}
                              step={0.5}
                              value={ot.working_hours ?? 8}
                              disabled={disabled}
                              onChange={(e) => patchOt({ working_hours: e.target.value })}
                            />
                          </Field>
                          <Field
                            label="OT Pay Rate (%)"
                            required
                            full
                            error={errors.ot_pay_rate_pct}
                            hint="OT Pay Rate determines how much of the calculated regular hourly rate is paid for each approved OT hour."
                          >
                            <div className="ewc-rate-presets">
                              {OT_PAY_RATE_PRESETS.map((p) => (
                                <button
                                  key={p.value}
                                  type="button"
                                  className={`ewc-rate-btn${Number(ot.ot_pay_rate_pct) === p.value ? " ewc-rate-btn--on" : ""}`}
                                  disabled={disabled}
                                  onClick={() => patchOt({ ot_pay_rate_pct: p.value })}
                                >
                                  {p.label}
                                </button>
                              ))}
                            </div>
                            <label className="ewc-label" style={{ marginTop: 8 }}>Custom: ___ %</label>
                            <InputWithIcon
                              icon={Settings2}
                              type="number"
                              min={1}
                              max={500}
                              value={ot.ot_pay_rate_pct ?? 150}
                              disabled={disabled}
                              onChange={(e) => patchOt({ ot_pay_rate_pct: e.target.value })}
                            />
                          </Field>
                        </div>
                      ) : null}

                      {ot.method === "fixed_rate" ? (
                        <div className="ewc-grid" style={{ marginTop: 12 }}>
                          <Field label="Fixed Amount Based On" required error={errors.ot_rate_unit} full>
                            <div className="ewc-choice-grid ewc-choice-grid--compact">
                              {[
                                { value: "per_hour", label: "Per OT Hour", hint: "Hours × amount" },
                                { value: "per_event", label: "Per OT Event", hint: "Each approved OT record gets this amount, regardless of hours." },
                                {
                                  value: "per_occurrence",
                                  label: "Fixed Amount Per OT Occurrence",
                                  hint: "Track OT days with checkboxes (10 by default). Each ticked occurrence pays the fixed amount.",
                                },
                              ].map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={`ewc-choice${(ot.rate_unit || "per_hour") === opt.value ? " ewc-choice--on" : ""}`}
                                  disabled={disabled}
                                  onClick={() =>
                                    patchOt({
                                      rate_unit: opt.value,
                                      ...(opt.value === "per_occurrence"
                                        ? {
                                            occurrence_slots_default: ot.occurrence_slots_default || DEFAULT_OCCURRENCE_SLOTS,
                                            occurrence_expand_roles:
                                              ot.occurrence_expand_roles?.length
                                                ? ot.occurrence_expand_roles
                                                : ["hr", "manager", "admin", "superadmin"],
                                          }
                                        : {}),
                                    })
                                  }
                                >
                                  <span className="ewc-choice__label">{opt.label}</span>
                                  <span className="ewc-choice__hint">{opt.hint}</span>
                                </button>
                              ))}
                            </div>
                          </Field>
                          <Field
                            label={
                              (ot.rate_unit || "per_hour") === "per_occurrence"
                                ? "OT Amount Per Occurrence (₹)"
                                : (ot.rate_unit || "per_hour") === "per_event"
                                  ? "OT Amount Per Event (₹)"
                                  : "OT Amount Per Hour (₹)"
                            }
                            required
                            error={errors.ot_fixed_amount}
                          >
                            <InputWithIcon
                              icon={Wallet}
                              type="number"
                              min={0}
                              step={1}
                              value={ot.fixed_amount ?? 0}
                              disabled={disabled}
                              onChange={(e) => patchOt({ fixed_amount: e.target.value })}
                            />
                          </Field>
                          {ot.rate_unit === "per_occurrence" ? (
                            <>
                              <Field
                                label="Default occurrence checkboxes"
                                required
                                error={errors.ot_occurrence_slots}
                                hint={`Shown first on Attendance → Overtime (1–${MAX_OCCURRENCE_SLOTS}). Authorized roles can enroll more per employee/month.`}
                              >
                                <InputWithIcon
                                  icon={Settings2}
                                  type="number"
                                  min={1}
                                  max={MAX_OCCURRENCE_SLOTS}
                                  value={ot.occurrence_slots_default ?? DEFAULT_OCCURRENCE_SLOTS}
                                  disabled={disabled}
                                  onChange={(e) => patchOt({ occurrence_slots_default: e.target.value })}
                                />
                              </Field>
                              <Field
                                label="Who may increase occurrence checkboxes"
                                required
                                full
                                error={errors.ot_occurrence_roles}
                                hint="Super Admin decides which roles can enroll extra OT ticks for an employee in a month."
                              >
                                <div className="ewc-chips" style={{ marginTop: 6 }}>
                                  {OCCURRENCE_EXPAND_ROLE_OPTIONS.map((r) => {
                                    const on = (ot.occurrence_expand_roles || []).includes(r.value);
                                    return (
                                      <button
                                        key={r.value}
                                        type="button"
                                        className={`ewc-chip${on ? " ewc-chip--on" : ""}`}
                                        disabled={disabled || (!isSuperAdmin && !canEdit)}
                                        onClick={() => {
                                          const cur = Array.isArray(ot.occurrence_expand_roles)
                                            ? [...ot.occurrence_expand_roles]
                                            : [];
                                          const next = on ? cur.filter((x) => x !== r.value) : [...cur, r.value];
                                          patchOt({ occurrence_expand_roles: next });
                                        }}
                                      >
                                        {on ? "✓ " : ""}
                                        {r.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </Field>
                              <p className="ewc-hint" style={{ gridColumn: "1 / -1" }}>
                                On Attendance → Overtime, each employee shows occurrence checkboxes. Tick 1 = first OT day,
                                tick 2 = second OT day, and so on. Payroll pays{" "}
                                <strong>Approved</strong> ticks × amount above.
                              </p>
                            </>
                          ) : null}
                        </div>
                      ) : null}

                      {ot.method === "formula" ? (
                        <div style={{ marginTop: 12 }}>
                          <Field label="Custom Formula" required error={errors.ot_formula} full hint="Open the Overtime Formula Builder. The formula decides the OT amount. OT hours still come from Attendance.">
                            <InputWithIcon
                              icon={Settings2}
                              value={ot.formula_expression || ""}
                              disabled={disabled}
                              placeholder="(Basic Salary ÷ 26 ÷ 8) × OT Hours × 1.5"
                              onChange={(e) => patchOt({ formula_expression: e.target.value })}
                            />
                          </Field>
                          <button
                            type="button"
                            className="ewc-btn ewc-btn--ghost"
                            style={{ marginTop: 8 }}
                            disabled={disabled}
                            onClick={() => navigate(OVERTIME_FORMULA_BUILDER_PATH, { state: { extraWork: form } })}
                          >
                            Open Formula Builder
                          </button>
                          <p className="ewc-hint" style={{ marginTop: 8 }}>
                            Available variables: Basic, Gross, DA, OT Hours, OT Count, OT Days, OT Events, Working Days, Working Hours, OT Pay Rate, Fixed OT Amount. Use OT Count when allocation is by OT.
                          </p>
                        </div>
                      ) : null}

                      {otPreview.lines?.length ? (
                        <div className="ewc-preview" style={{ marginTop: 16 }}>
                          <h4><Eye size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Calculation Preview</h4>
                          <dl>
                            {otPreview.lines.map((row) => (
                              <div key={row.label} className={row.emphasize ? "ewc-preview__emph" : undefined}>
                                <dt>{row.label}</dt>
                                <dd>{row.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <Building2 className="ewc-card__icon text-sky-700" aria-hidden />
                <div>
                  <h3>Department-wise OT rate</h3>
                  <p>Give each department its own percent. One employee can still have a different rate on the Overtime page.</p>
                </div>
              </div>
              <div className="ewc-toggles">
                <Toggle
                  label="Use a different OT rate for each department"
                  hint="Example: Admin 100%, HR 150%, Development 200%. A department you leave blank keeps the organisation rate."
                  checked={Boolean(ot.department_rates_enabled)}
                  disabled={disabled || !otEnabled}
                  onChange={(v) => patchOt({ department_rates_enabled: v })}
                />
              </div>
              {ot.department_rates_enabled ? (
                <DepartmentRateList
                  departments={departments}
                  ot={ot}
                  disabled={disabled || !otEnabled}
                  onChange={(rows) => patchOt({ department_rates: rows })}
                />
              ) : (
                <p className="ewc-hint" style={{ marginTop: 12 }}>
                  Every department uses the organisation OT rate above. You can still change one employee from Attendance → Overtime.
                </p>
              )}
              {errors.ot_department_rates ? <p className="ewc-error">{errors.ot_department_rates}</p> : null}
              <div className="ewc-toggles" style={{ marginTop: 14 }}>
                <Toggle
                  label="Different working hours for each department"
                  hint="Off: every department uses the organisation working hours (1 day). On: HR can be 8 hours and Development 10 hours."
                  checked={Boolean(ot.department_hours_enabled)}
                  disabled={disabled || !otEnabled}
                  onChange={(v) => patchOt({ department_hours_enabled: v })}
                />
              </div>
              {ot.department_hours_enabled ? (
                <DepartmentHoursList
                  departments={departments}
                  ot={ot}
                  disabled={disabled || !otEnabled}
                  onChange={(rows) => patchOt({ department_hours: rows })}
                />
              ) : (
                <p className="ewc-hint" style={{ marginTop: 12 }}>
                  Every department uses {ot.working_hours || 8} hours as one working day.
                </p>
              )}
              {errors.ot_department_hours ? <p className="ewc-error">{errors.ot_department_hours}</p> : null}
            </section>

            <section className={`ewc-card${eligibilityLocked ? " ewc-card--locked" : ""}`}>
              <div className="ewc-card__head">
                <Clock className="ewc-card__icon text-indigo-600" aria-hidden />
                <div>
                  <h3>Overtime Eligibility Rules</h3>
                  <p>
                    {eligibilityLocked
                      ? "Turned off while OT hours are entered manually."
                      : "Minimum thresholds, daily/monthly caps, rounding, and ignore-below policy."}
                  </p>
                </div>
              </div>
              {eligibilityLocked ? (
                <div className="ewc-disabled-banner" role="status">
                  Minimum OT, maximum OT, rounding, and ignore-below rules apply only when OT hours are calculated automatically from attendance. They stay off for Manual Entry.
                </div>
              ) : null}
              <div className={`ewc-grid${eligibilityLocked ? " ewc-rules--off" : ""}`}>
                <Field label="Minimum OT Minutes" error={eligibilityApplies ? errors.min_ot_minutes : null}>
                  <InputWithIcon
                    icon={Timer}
                    type="number"
                    min={0}
                    value={elig.min_ot_minutes ?? 30}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { min_ot_minutes: e.target.value })}
                  />
                </Field>
                <Field label="Minimum OT Hours">
                  <InputWithIcon
                    icon={Timer}
                    type="number"
                    min={0}
                    step={0.25}
                    value={elig.min_ot_hours ?? 0}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { min_ot_hours: e.target.value })}
                  />
                </Field>
                <Field label="Maximum OT Per Day" error={eligibilityApplies ? errors.max_ot_hours_per_day : null}>
                  <InputWithIcon
                    icon={Timer}
                    type="number"
                    min={0}
                    step={0.5}
                    value={elig.max_ot_hours_per_day ?? 4}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { max_ot_hours_per_day: e.target.value })}
                  />
                </Field>
                <Field label="Maximum OT Per Month" error={eligibilityApplies ? errors.max_ot_hours_per_month : null}>
                  <InputWithIcon
                    icon={Timer}
                    type="number"
                    min={0}
                    step={0.5}
                    value={elig.max_ot_hours_per_month ?? 48}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { max_ot_hours_per_month: e.target.value })}
                  />
                </Field>
                <Field label="Round OT">
                  <SelectWithIcon
                    icon={Clock}
                    value={elig.round_ot || "nearest_15"}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { round_ot: e.target.value })}
                  >
                    {roundMethods.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </SelectWithIcon>
                </Field>
                <Field label="Ignore Below Threshold (minutes)" error={eligibilityApplies ? errors.ignore_below_minutes : null} hint="Example: ignore OT below 30 minutes">
                  <InputWithIcon
                    icon={Timer}
                    type="number"
                    min={0}
                    value={elig.ignore_below_minutes ?? 30}
                    disabled={eligibilityDisabled}
                    onChange={(e) => patch("eligibility", { ignore_below_minutes: e.target.value })}
                  />
                </Field>
              </div>
            </section>

            <section className="ewc-card">
              <div className="ewc-card__head">
                <ShieldCheck className="ewc-card__icon text-emerald-600" aria-hidden />
                <div>
                  <h3>OT Payroll Processing</h3>
                  <p>How approved OT from Attendance enters the payroll run.</p>
                </div>
              </div>
              <div className="ewc-toggles">
                <Toggle
                  label="Require approval before OT enters payroll"
                  hint="Pending / Rejected / Cancelled OT is never paid. Only Approved OT hours are calculated."
                  checked={proc.require_ot_approval !== false}
                  disabled={disabled || !otEnabled}
                  onChange={(v) => patch("processing", { require_ot_approval: v })}
                />
              </div>
              <ul className="ewc-engine" style={{ marginTop: 12 }}>
                <li>Read Enable Overtime, OT Hours Source, and OT allocation from this page</li>
                <li>Attendance records and approves OT (Attendance → Overtime)</li>
                <li>Hours allocation pays the calculated hours. OT-count allocation pays the OT count.</li>
                <li>Pays through the single OVERTIME earning component</li>
              </ul>
            </section>
          </div>

          <aside className="ewc-summary" aria-label="Preview overtime configuration summary">
            <h3><Eye size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Preview Summary</h3>
            <dl>
              <div><dt>Overtime</dt><dd>{summary.otEnabled}</dd></div>
              <div><dt>OT Hours Source</dt><dd>{summary.hoursSource}</dd></div>
              <div><dt>OT Allocation</dt><dd>{summary.allocation}</dd></div>
              <div><dt>OT Pay Method</dt><dd>{summary.otMethod}</dd></div>
              <div><dt>Maximum OT Per Day</dt><dd>{summary.maxOtPerDay}</dd></div>
              <div><dt>Maximum OT Per Month</dt><dd>{summary.maxOtPerMonth}</dd></div>
              <div><dt>Department rates</dt><dd>{summary.departmentRates}</dd></div>
              <div><dt>Pays through</dt><dd>OVERTIME component</dd></div>
            </dl>
            {dirty ? (
              <p className="ewc-hint" style={{ marginTop: 10 }}>
                <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
                Unsaved — review before leaving this page.
              </p>
            ) : null}
          </aside>
        </div>
      )}

      {canEdit && !loading ? (
        <div className="ewc-footer-bar" role="toolbar" aria-label="Overtime Works actions">
          <button type="button" className="ewc-btn ewc-btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <span className="ewc-footer-bar__spacer" />
          <button type="button" className="ewc-btn ewc-btn--ghost" onClick={onReset} disabled={saving || !dirty}>
            <RotateCcw size={14} /> Reset
          </button>
          <button type="button" className="ewc-btn ewc-btn--danger" onClick={onRestoreDefault} disabled={saving}>
            Restore Default
          </button>
          <button type="button" className="ewc-btn ewc-btn--primary" onClick={onSave} disabled={saving || loading}>
            <Cog size={15} /> {saving ? "Saving…" : "Save Configuration"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
